/**
 * @fileoverview One row of the Audit Log table.
 * @description A full-width button (so it is keyboard-focusable and announced as actionable) laid
 * out on the table's shared grid: when, who (the stored, masked name with the actor type and id
 * beneath), the event (readable name with its exact code beneath, which is what the Event type
 * filter matches), the resource it touched, the module that published it, and the result badge.
 * Clicking opens the event's detail.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { AUDIT_TABLE_GRID_CLASS } from '@/features/audit/constants/audit-table.constants'
import {
  ACTOR_TYPE_LABEL_KEY,
  describeResource,
  formatOccurredAt,
  humanizeCode,
  RESULT_LABEL_KEY,
  RESULT_VARIANT,
} from '@/features/audit/utils/audit-display.utils'
import Badge from '@/shared/components/atoms/Badge'

import type { AuditEventSummary } from '@/features/audit/types/audit.types'

interface AuditEventRowProps {
  event: AuditEventSummary
  /** The active language tag, for the timestamp. */
  locale: string
  onSelect: (auditEventId: string) => void
}

/**
 * @component
 * @param {AuditEventRowProps} props - The event to show and what clicking it does.
 * @param {AuditEventSummary} props.event - The event's summary.
 * @param {string} props.locale - The active language tag, for the timestamp.
 * @param {(auditEventId: string) => void} props.onSelect - Called with the event's id on click.
 */
function AuditEventRow({ event, locale, onSelect }: AuditEventRowProps) {
  const { t } = useTranslation()
  const { actor } = event
  const actorName =
    actor.displayName ??
    (actor.actorType === 'SYSTEM'
      ? t('audit.actor.system')
      : (actor.actorId ?? t('audit.actor.unknown')))
  const actorMeta = [
    t(ACTOR_TYPE_LABEL_KEY[actor.actorType]),
    actor.displayName ? actor.actorId : undefined,
  ]
    .filter(Boolean)
    .join(' · ')

  /**
   * @description Opens this event's detail.
   */
  const handleClick = () => {
    onSelect(event.auditEventId)
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="audit-row"
      className={`${AUDIT_TABLE_GRID_CLASS} w-full cursor-pointer items-center gap-3 border-t border-row-border px-4 py-3 text-left hover:bg-surface-hover sm:px-5`}
    >
      <span className="text-[13px] tabular-nums text-ink" data-testid="audit-occurred-at">
        {formatOccurredAt(event.occurredAt, locale)}
      </span>

      <span className="flex min-w-0 flex-col">
        <span
          title={actorName}
          className="truncate text-[13.5px] font-semibold text-ink"
          data-testid="audit-actor-name"
        >
          {actorName}
        </span>
        <span className="truncate text-xs text-slate" data-testid="audit-actor-meta">
          {actorMeta}
        </span>
      </span>

      <span className="flex min-w-0 flex-col">
        <span
          className="truncate text-[13.5px] font-medium text-ink"
          data-testid="audit-event-name"
        >
          {humanizeCode(event.eventType)}
        </span>
        <span
          className="truncate font-mono text-[11.5px] text-slate"
          data-testid="audit-event-type"
        >
          {event.eventType}
        </span>
      </span>

      <span
        title={describeResource(event.resource)}
        className="truncate font-mono text-[12px] text-slate"
        data-testid="audit-resource"
      >
        {describeResource(event.resource)}
      </span>

      <span
        title={event.sourceModule}
        className="truncate font-mono text-[12px] text-slate"
        data-testid="audit-module"
      >
        {event.sourceModule}
      </span>

      <span>
        <Badge variant={RESULT_VARIANT[event.result]} dot data-testid="audit-result-badge">
          {t(RESULT_LABEL_KEY[event.result])}
        </Badge>
      </span>
    </button>
  )
}

export default AuditEventRow
